<script setup>
import { ref, watch, toRef, computed, useTemplateRef, nextTick } from 'vue'
import { useMessageStack } from '@/stores/messagestack'
import { useUserStore } from '@/stores/user'
import { useApi } from '@/composables/api.js'
import config from '@/config'
import PDFView from './pdf.vue'
import HTMLView from './html.vue'
import IMGView from './img.vue'
import { getExtension, extensions, extensionsImages } from './view.js'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const emit = defineEmits(['page', 'loaded'])
const props = defineProps({
  upload: Object,
  /** Прямая ссылка на файл/PDF (blob: или URL) — без convert через upload API */
  link: {
    type: String,
    default: null,
  },
  format: {
    type: String,
    default: 'pdf',
  },
  scale: {
    type: Number,
    default: 1,
  },
  rotation: {
    type: Number,
    default: 0,
  },
})
const { push: notify } = useMessageStack()
const userStore = useUserStore()

const dnLink = ref(null)
const upLink = ref(null)
const loading = ref(true)
const scale = toRef(props, 'scale')
const viewEl = useTemplateRef('view-el')
const fileOk = computed(
  () => !!props.link || extensions.includes(getExtension(props.upload))
)
const isPDF = computed(
  () => !!props.link || getExtension(props.upload) == 'pdf'
)
const isIMG = computed(
  () => !props.link && extensionsImages.includes(getExtension(props.upload))
)
const apiBase = computed(() => props.upload?.api_base || null)

const { status, refresh, model } = useApi({
  url: upLink,
  baseUrl: apiBase,
})

watch(
  [() => props.upload, () => props.link, isPDF, isIMG, fileOk, () => props.format],
  ([u, link, isPDF, isIMG, fileOk, format]) => {
    loading.value = true
    dnLink.value = null
    if (link) {
      dnLink.value = link
      return
    }

    if (!u) {
      return
    }
    if (!fileOk) {
      loading.value = false
      return
    }
    if (isIMG || (u.converted?.in_progress == 1 && u.converted?.format === format)) {
      setDnLink()
      return
    }
    if (u.converted?.in_progress == 2 && u.converted?.format === format) {
      model.value = { message: u.converted.status }
      setError()
      return
    }
    upLink.value = `upload/${u.secure_filename}/convert/${format}`
    refresh().catch(notify)
  },
  { immediate: true }
)

watch([status], ([status]) => {
  if (status == 202) {
    setTimeout(() => {
      refresh().catch(notify)
    }, 1000)
  }
  if (status == 422) {
    setError()
  }
  if (status == 204) {
    setDnLink()
  }
})

defineExpose({
  scale,
  jumpTo: n => {
    emit('page', n)
    nextTick(() => viewEl.value.jumpTo(n))
  },
  /**
   * @param {number} dpi
   * @param {string} filename
   * @param {(canvas: HTMLCanvasElement) => void} [prePrintCb]
   * @param {boolean} [withDiraStamp] печать PDF выгрузки со штампом (как download без inline)
   */
  print: async (dpi, filename, prePrintCb, withDiraStamp = false) => {
    const override =
      withDiraStamp && !isIMG.value ? getExportDownloadLink() : null
    return viewEl.value.print(dpi, filename, prePrintCb, override)
  },
})
function setDnLink() {
  // inline=1 — без DIRA-штампа; штамп только при скачивании (без inline).
  // AS-1904: для PDF тоже /convert/pdf — отдаёт secure_filename.pdf (нормализованный),
  // а не оригинал через /download/?inline=1 (там был тихий fallback на исходник).
  const base = props.upload.api_base || config.VITE_API_URL
  let link = `${base}download/${props.upload.secure_filename}?inline=1`
  if (fileOk.value && !isIMG.value) {
    link = `${base}download/${props.upload.secure_filename}/convert/${props.format}?inline=1`
  }
  // PDF.js / <img> не шлют Authorization; для чужого origin (tickets) — token в query
  const token = userStore.user?.access_token
  if (props.upload.api_base && token) {
    const sep = link.includes('?') ? '&' : '?'
    link = `${link}${sep}access_token=${encodeURIComponent(token)}`
  }
  dnLink.value = link
}

function getExportDownloadLink() {
  // Выгрузка/печать со штампом — оригинал PDF; office по-прежнему через convert.
  let link = `${config.VITE_API_URL}download/${props.upload.secure_filename}`
  if (fileOk.value && !isIMG.value && !isPDF.value) {
    link = `${config.VITE_API_URL}download/${props.upload.secure_filename}/convert/${props.format}`
  }
  return link
}
function setError() {
  loading.value = false
}
function onLoaded(pages, scale, orientation) {
  loading.value = false
  emit('loaded', pages, scale, orientation)
}
</script>
<template>
  <div
    class="border position-relative doc-view-view"
    :class="{ 'overflow-hidden': loading, 'overflow-y-auto': !loading }"
  >
    <div
      class="position-absolute w-100 h-100 d-flex align-items-center justify-content-center modal-backdrop"
      v-if="loading"
    >
      <span class="spinner-border"> </span>
    </div>
    <div
      class="alert alert-danger"
      v-if="!loading && model"
    >
      <h4 class="alert-heading">{{ t('common.error') }}</h4>
      <p>{{ t('loadingError') }}</p>
      <p>{{ model.message }}</p>
    </div>
    <template v-if="!fileOk">
      <p class="alert alert-info">
        {{ t('unsupportedFormat') }}
      </p>
    </template>
    <template v-if="dnLink && fileOk">
      <PDFView
        :link="dnLink"
        :scale="scale"
        :rotation="rotation"
        v-if="!isIMG && (isPDF || String(format).startsWith('pdf'))"
        ref="view-el"
        @page="(curr, total) => $emit('page', curr, total)"
        @loaded="onLoaded"
      />
      <HTMLView
        :link="dnLink"
        :scale="scale"
        v-if="!isPDF && !isIMG && format == 'html'"
        ref="view-el"
        @loaded="onLoaded"
      />
      <IMGView
        :link="dnLink"
        :scale="scale"
        v-if="isIMG"
        ref="view-el"
        @loaded="onLoaded"
      />
    </template>
  </div>
</template>

<i18n lang="json">
{
  "ru": {
    "loadingError": "Во время загрузки файла произошла ошибка",
    "unsupportedFormat": "Просмотр данного формата файла не поддерживается. Вы можете его скачать"
  },
  "en": {
    "loadingError": "An error occurred while loading the file",
    "unsupportedFormat": "Viewing this file format is not supported. You can download it instead"
  }
}
</i18n>
